import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Accessibility, Menu, Volume2, Square, Cloud, CloudOff, LogIn } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useA11y } from "@/context/A11yContext";
import { useActiveSection } from "@/hooks/useStoredState";
import { useProgress, FILLABLE } from "@/lib/progress";
import { TOOLS } from "@/data/tools";
import { scrollToId } from "@/lib/scroll";
import { LogoMark } from "@/components/Reveal";
import { A11yPanel } from "@/components/A11yPanel";
import { useWorkspace } from "@/context/WorkspaceContext";

const CLOUD_TEXT = { saving: "Saving…", saved: "Saved", error: "Not saved" };

const WorkspaceBadge = () => {
  const { ws } = useWorkspace();
  const [status, setStatus] = useState("saved");
  useEffect(() => {
    const f = (e) => setStatus(e.detail);
    window.addEventListener("sap:cloud", f);
    return () => window.removeEventListener("sap:cloud", f);
  }, []);
  if (!ws) {
    return (
      <Link to="/workspace" className="btn btn-ghost btn-sm" data-testid="topbar-workspace-signin" aria-label="Sign in to your school workspace">
        <LogIn size={16} aria-hidden /> <span className="hidden md:inline">Sign in</span>
      </Link>
    );
  }
  const view = ws.user.role === "governor";
  return (
    <Link to="/workspace" className="btn btn-ghost btn-sm max-w-[220px]" data-testid="topbar-workspace-badge" aria-live="polite"
      aria-label={`${ws.org.name} workspace. ${view ? "View only" : CLOUD_TEXT[status] || "Saved"}`}>
      {status === "error" ? <CloudOff size={16} aria-hidden /> : <Cloud size={16} aria-hidden />}
      <span className="hidden truncate md:inline">{ws.org.name} · {view ? "View only" : CLOUD_TEXT[status] || "Saved"}</span>
    </Link>
  );
};

const MAIN = [
  { id: "toolkits", label: "Toolkits" },
  { id: "toolkit-one", label: "Toolkit 2: SENDCO Pack" },
  { id: "feedback", label: "Feedback" },
];

const NavLink = ({ id, children, minimal, className, current, onDone }) => (
  <a href={minimal ? `/#${id}` : `#${id}`} aria-current={current ? "true" : undefined} className={className}
    data-testid={`nav-${id}`}
    onClick={(e) => {
      if (minimal) return;
      e.preventDefault();
      scrollToId(id);
      onDone?.();
    }}>
    {children}
  </a>
);

const ReadAloud = () => {
  const { speak, stop, speaking, canSpeak } = useA11y();
  if (!canSpeak) return null;
  const go = () => {
    if (speaking) return stop();
    const sel = window.getSelection()?.toString().trim();
    return speak(sel || document.querySelector("main")?.innerText || "");
  };
  return (
    <button type="button" onClick={go} className="btn btn-ghost btn-sm" aria-label={speaking ? "Stop reading aloud" : "Read page aloud"} data-testid="topbar-read-aloud">
      {speaking ? <Square size={16} aria-hidden /> : <Volume2 size={16} aria-hidden />}
      <span className="hidden sm:inline">{speaking ? "Stop" : "Read aloud"}</span>
    </button>
  );
};

const ProgressBadge = () => {
  const n = useProgress();
  return (
    <span className="mr-2 flex shrink-0 items-center gap-2 rounded-full border-2 border-line px-3 py-1 text-[13px] font-bold" data-testid="progress-badge" aria-live="polite">
      <span className="h-2 w-12 overflow-hidden rounded-full bg-line" aria-hidden="true">
        <span className="block h-full bg-brand transition-[width] duration-500" style={{ width: `${(n / FILLABLE.length) * 100}%` }} />
      </span>
      {n} of {FILLABLE.length} sections started
    </span>
  );
};

const T1_INDEX = { label: "Toolkit 2 index", items: [...TOOLS.map((t) => ({ id: t.id, n: t.n, short: t.short })), { id: "feedback", short: "Feedback" }] };
T1_INDEX.ids = T1_INDEX.items.map((i) => i.id);

const IndexStrip = ({ minimal, index }) => {
  const idx = index || T1_INDEX;
  const local = index ? false : minimal;
  const active = useActiveSection(idx.ids);
  return (
    <nav aria-label={idx.label} className={`border-t border-line ${minimal && !index ? "hidden lg:block" : ""}`} data-testid="index-strip">
      <div className="no-scrollbar mx-auto flex h-11 max-w-[1400px] items-center gap-1 overflow-x-auto px-4 sm:px-6 lg:px-10">
        {!minimal && !index && <ProgressBadge />}
        <span className="eyebrow mr-2 hidden shrink-0 xl:inline">{idx.label}</span>
        {idx.items.map((t) => (
          <NavLink key={t.id} id={t.id} minimal={local} className="chip" current={active === t.id}>
            {t.n && <span className="font-display text-xs opacity-80">{t.n}</span>} {t.short}
          </NavLink>
        ))}
      </div>
    </nav>
  );
};

const MOBILE_LINK = "flex min-h-11 items-center rounded-xl px-3 py-2.5 font-bold hover:bg-paper";
const TOOLKIT_LINKS = [
  ["/quick-start", "Toolkit 1: Free Quick Start Pack", "quick-start"],
  ["/#toolkit-one", "Toolkit 2: SENDCO Resource Pack", "toolkit-one"],
  ["/workbook", "Quick Start Improvement Workbook", "workbook"],
  ["/toolkit-3", "Toolkit 3: Operational Framework", "toolkit-3"],
  ["/toolkit-4", "Toolkit 4: Improvement Planner", "toolkit-4"],
  ["/printables", "Classroom printables", "printables"],
  ["/poster", "Quick Start poster", "poster"],
];
const MORE_LINKS = [
  ["/resource-centre", "SENDCO Resource Centre", "resource-centre"],
  ["/workspace", "School workspace sign in", "workspace"],
  ["/resources", "Resource directory", "resources"],
  ["/feedback", "Give feedback", "feedback-page"],
  ["/contact", "Contact us", "contact"],
];
const MenuGroup = ({ title, links, onPick }) => (
  <div className="mt-6">
    <p className="eyebrow mb-2 px-3">{title}</p>
    {links.map(([to, label, id]) => (
      <Link key={id} to={to} className={MOBILE_LINK} data-testid={`mobile-nav-${id}`} onClick={(e) => onPick(e, to)}>{label}</Link>
    ))}
  </div>
);

const MobileMenu = ({ minimal }) => {
  const [open, setOpen] = useState(false);
  const pick = (e, to) => {
    setOpen(false);
    if (!minimal && to === "/#toolkit-one") { e.preventDefault(); scrollToId("toolkit-one"); }
  };
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button type="button" className="btn btn-ghost btn-sm lg:hidden" aria-label="Open menu" data-testid="topbar-menu-button">
          <Menu size={18} aria-hidden />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[88vw] max-w-sm overflow-y-auto border-line bg-surface text-ink" data-lenis-prevent>
        <SheetTitle className="font-display text-2xl text-ink">Menu</SheetTitle>
        <SheetDescription className="text-ink-soft">Jump to any toolkit or change accessibility settings.</SheetDescription>
        <nav aria-label="Mobile" className="flex flex-col">
          <MenuGroup title="Toolkits" links={TOOLKIT_LINKS} onPick={pick} />
          <MenuGroup title="More" links={MORE_LINKS} onPick={pick} />
          {!minimal && (
            <div className="mt-6">
              <p className="eyebrow mb-2 px-3">SENDCO pack sections</p>
              {TOOLS.map((t) => <NavLink key={t.id} id={t.id} className={MOBILE_LINK} onDone={() => setOpen(false)}>{`${t.n} ${t.title}`}</NavLink>)}
            </div>
          )}
        </nav>
        <div className="mt-8 border-t border-line pt-6"><A11yPanel /></div>
      </SheetContent>
    </Sheet>
  );
};

export const TopBar = ({ minimal = false, index }) => (
  <header data-topbar className="fixed inset-x-0 top-0 z-50">
    <a href="#main" className="skip-link" data-testid="skip-link">Skip to main content</a>
    <div className="topbar-glass border-b border-line">
      <div className="mx-auto flex h-16 max-w-[1400px] items-center gap-3 px-4 sm:px-6 lg:px-10">
        <Link to="/" className="flex shrink-0 items-center gap-2.5" data-testid="topbar-logo" aria-label="The SEND Accountability Project, home">
          <LogoMark className="h-9 w-9" />
          <span className="hidden leading-[1.05] min-[400px]:block">
            <span className="block font-display text-[15px] font-semibold">The SEND</span>
            <span className="block text-[12px] font-bold text-ink-soft">Accountability Project</span>
          </span>
        </Link>
        <nav aria-label="Main" className="ml-6 hidden items-center gap-1 lg:flex">
          <Link to="/quick-start" className="chip" data-testid="nav-quick-start">Toolkit 1: Quick Start</Link>
          {MAIN.map((m) => (
            <NavLink key={m.id} id={m.id} minimal={minimal} className="chip">{m.label}</NavLink>
          ))}
          <Link to="/resource-centre" className="chip" data-testid="nav-resource-centre">Resource Centre</Link>
        </nav>
        <div className="ml-auto flex items-center gap-2">
          <WorkspaceBadge />
          <ReadAloud />
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" className="btn btn-brand btn-sm" data-testid="topbar-a11y-button" aria-label="Accessibility options">
                <Accessibility size={16} aria-hidden /> <span className="hidden sm:inline">Accessibility</span>
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" sideOffset={10} className="max-h-[80vh] w-[340px] overflow-y-auto rounded-2xl border-2 border-line bg-surface p-5 text-ink" data-lenis-prevent>
              <A11yPanel />
            </PopoverContent>
          </Popover>
          <MobileMenu minimal={minimal} />
        </div>
      </div>
      <IndexStrip minimal={minimal} index={index} />
    </div>
  </header>
);
